import { Injectable } from '@angular/core';
import { ModalController, Platform } from '@ionic/angular';
import { WorkListEntry } from '../lib/models/work-list-entry';
import { ExportPopupComponent } from '../lib/export-popup/export-popup.component';
import { HoursDetailComponent } from '../lib/hours/hours-detail/hours-detail.component';
import { HoursFormComponent } from '../lib/hours/hours-form/hours-form.component';
import { AbsenceDetailComponent } from '../lib/absence/absence-detail/absence-detail.component';
import { AbsenceFormComponent } from '../lib/absence/absence-form/absence-form.component';
import { MissingWorkEntryComponent } from '../lib/missing-work-entry/missing-work-entry.component';
import { ViewerDialogComponent } from '../lib/viewer-dialog/viewer-dialog.component';
import { ModalRole } from '../lib/models/modal-roles';
import { PdfType } from '../lib/models/pdf-type';
import { InspectionDetailComponent } from '../lib/machinery/inspection-detail/inspection-detail.component';
import { InspectionFormComponent } from '../lib/machinery/inspection-form/inspection-form.component';
import { ConstructionDayReportDetailComponent } from '../lib/construction-day-report/construction-day-report-detail/construction-day-report-detail.component';
import { ConstructionDayReportFormComponent } from '../lib/construction-day-report/construction-day-report-form/construction-day-report-form.component';
import { UploadItem } from '../lib/models/upload-item';
import { NodeItem } from '../api/models';
import { UploadModalComponent } from '../lib/upload-modal/upload-modal.component';

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  constructor(
    private modalController: ModalController,
    public platform: Platform
  ) {}

  public async openHoursDetailModal(
    workListEntry: WorkListEntry,
    isBigScreen: boolean
  ) {
    if (workListEntry.isAbsence) {
      await this.openAbsenceDetailModal(workListEntry.id);
      return;
    }

    const modal = await this.modalController.create({
      component: HoursDetailComponent,
      componentProps: {
        id: workListEntry.id,
      },
      //cssClass:
      //  this.platform.is('desktop') || this.platform.is('tablet')
      //   ? 'custom-desktop-modal'
      //  : '',
    });
    modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (role === 'confirm') {
      if (data == 'edit') {
        this.openHoursFormModal(workListEntry.id, isBigScreen);
      }
    }
  }

  public async openHoursFormModal(id: string, isBigScreen: boolean) {
    const modal = await this.modalController.create({
      component: HoursFormComponent,
      componentProps: {
        workDayId: id,
      },
      cssClass: isBigScreen ? 'custom-desktop-modal' : '',
    });
    modal.present();

    const { data, role } = await modal.onWillDismiss();
  }

  public async createExportModal(
    monthYearLabel: string,
    month: number,
    year: number
  ) {
    const modal = await this.modalController.create({
      component: ExportPopupComponent,
      componentProps: {
        monthYearLabel: monthYearLabel,
        month: month,
        year: year,
      },
    });

    modal.present();
    return await modal.onWillDismiss();
  }

  public async openAbsenceDetailModal(id: string) {
    const modal = await this.modalController.create({
      component: AbsenceDetailComponent,
      componentProps: {
        paramId: id,
      },
    });
    modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (role === 'confirm') {
      if (data == 'edit') {
        this.openAbsenceFormModal(id);
      }
    }
  }

  public async openAbsenceFormModal(
    id: string,
    preselectedUser: string = null,
    preselectedUsers: string[] = [],
    preselectedDate: string = null
  ) {
    const modal = await this.modalController.create({
      component: AbsenceFormComponent,
      componentProps: {
        absenceEntryId: id,
        selectedUsers: preselectedUser
          ? [preselectedUser]
          : preselectedUsers ?? [],
        preselectedDate: preselectedDate,
      },
    });
    modal.present();

    const { data, role } = await modal.onWillDismiss();
  }

  public async openViewerDialog(
    ids: string[],
    pdfType: string,
    isBigScreen: boolean,
    index: number
  ) {
    const modal = await this.modalController.create({
      component: ViewerDialogComponent,
      componentProps: {
        docIds: ids,
        pdfType: pdfType,
        currentIndex: index,
      },
      cssClass: isBigScreen ? 'viewer-modal' : '',
    });
    modal.present();
  }

  public async openMissingWorkEntriesModal() {
    const modal = await this.modalController.create({
      component: MissingWorkEntryComponent,
    });
    modal.present();

    const { data, role } = await modal.onWillDismiss();
  }

  public async openInspectionDetailModal(id: string, isBigScreen: boolean) {
    const modal = await this.modalController.create({
      component: InspectionDetailComponent,
      componentProps: {
        paramId: id,
      },
    });
    modal.present();
    const { data, role } = await modal.onWillDismiss();
    if (role === ModalRole.Confirm) {
      if (data == ModalRole.Edit) {
        await this.openInspectionFormModal(id, isBigScreen);
      } else if (data == ModalRole.View) {
        await this.openViewerDialog([id], PdfType.Inspection, isBigScreen, 0);
      }
    }
  }

  public async openInspectionFormModal(id: string, isBigScreen: boolean) {
    const modal = await this.modalController.create({
      component: InspectionFormComponent,
      componentProps: {
        paramId: id,
      },
    });
    modal.present();
    const { data, role } = await modal.onWillDismiss();
    if (role === ModalRole.Confirm && id != null) {
      await this.openInspectionDetailModal(id, isBigScreen);
    }
  }

  public async openConstructionDayReportDetailModal(
    id: string,
    isBigScreen: boolean
  ) {
    const modal = await this.modalController.create({
      component: ConstructionDayReportDetailComponent,
      componentProps: {
        paramId: id,
      },
      cssClass: isBigScreen ? 'custom-desktop-modal' : '',
    });
    modal.present();

    const { data, role } = await modal.onWillDismiss();

    switch (role) {
      case ModalRole.Edit:
        this.openConstructionDayReportFormModal(data, isBigScreen);
        break;
      case ModalRole.View:
        await this.openViewerDialog(
          data,
          PdfType.ConstructionDayReport,
          isBigScreen,
          0
        );

        break;
    }
  }

  public async openConstructionDayReportFormModal(
    id: string,
    isBigScreen: boolean
  ) {
    const modal = await this.modalController.create({
      component: ConstructionDayReportFormComponent,
      componentProps: {
        reportId: id,
      },
      cssClass: isBigScreen ? 'custom-desktop-modal' : '',
    });
    modal.present();
    const { data, role } = await modal.onWillDismiss();
    if (role === ModalRole.Confirm && id != null)
      this.openConstructionDayReportDetailModal(id, isBigScreen);
  }

  public async openUploadModal(
    items: UploadItem[],
    currentFolder: NodeItem,
    files: FileList
  ) {
    const modal = await this.modalController.create({
      component: UploadModalComponent,
      componentProps: {
        files: items,
        fileList: files,
        currentFolder: currentFolder,
      },
    });
    modal.present();
    const { data, role } = await modal.onWillDismiss();
  }
}
